/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Handshake } from "lucide-react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { ReferralOverview } from "../components/referral/ReferralOverview";
import { PartnersTab } from "../components/referral/PartnersTab";
import { ApplicationsTab } from "../components/referral/ApplicationsTab";
import { CasesTab } from "../components/referral/CasesTab";
import { CommissionsTab } from "../components/referral/CommissionsTab";
import { RulesTab } from "../components/referral/RulesTab";
import { ReportsTab } from "../components/referral/ReportsTab";

const MGMT = ["admin", "ceo", "sales_vp", "sales_manager", "aftersales"];
const TABS = [
  { key: "overview", zh: "总览", en: "Overview", roles: MGMT },
  { key: "partners", zh: "介绍人档案", en: "Referrers", roles: [...MGMT, "marketing", "marketing_manager"] },
  { key: "applications", zh: "合作申请", en: "Applications", roles: MGMT },
  { key: "cases", zh: "推荐客户", en: "Referred leads", roles: [...MGMT, "marketing", "marketing_manager"] },
  { key: "commissions", zh: "佣金台账", en: "Commission ledger", roles: [...MGMT, "finance", "marketing", "marketing_manager"] },
  { key: "finance", zh: "财务工作台", en: "Finance desk", roles: ["admin", "finance", "ceo", "sales_vp"] },
  { key: "rules", zh: "规则 / 协议 / 设置", en: "Rules & settings", roles: MGMT },
  { key: "reports", zh: "渠道报表 / 异常", en: "Reports", roles: MGMT },
];
const PANEL_ROLES = [...MGMT, "finance", "marketing", "marketing_manager"];

export default function ReferralProgram() {
  const { lang, user, permOf } = useApp();
  const zh = lang === "zh";
  const [sp, setSp] = useSearchParams();
  // 新授予规则管理动作的账号也必须能找到规则页；已有管理岗位仍可只读查看。
  const tabs = useMemo(() => TABS.filter((t) => t.roles.includes(user?.role) || (t.key === "rules" && permOf("/referral::manage") === true) || (t.key === "finance" && permOf("/referral::finance-ops") === true)), [user?.role, permOf]);
  const tab = sp.get("tab") && tabs.some((t) => t.key === sp.get("tab")) ? sp.get("tab") : (tabs[0]?.key || "partners");
  useEffect(() => { if (!sp.get("tab") && tabs[0]) setSp({ tab: tabs[0].key }, { replace: true }); }, [sp, setSp, tabs]);
  const go = (key, extra = {}) => setSp({ tab: key, ...extra });
  const common = { api, zh, user, sp, go };
  // 【2026-10-26 扩面第六批】面板准入改读页面权限点 /referral（与后端 _need(key="/referral") 同一判定）；
  // 管理员给某岗位授「转介绍」页面后这里立即放行，屏蔽即隐藏。未加载时回落原名单。
  if (permOf("/referral") === undefined ? !PANEL_ROLES.includes(user?.role) : !permOf("/referral")) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center space-y-2" data-testid="referral-denied">
        <Handshake className="h-8 w-8 text-slate-300 mx-auto" />
        <p className="text-sm font-semibold text-slate-700">{zh ? "转介绍 / 合作伙伴面板仅对公司管理层与销售管理层开放" : "Referral panel is limited to management"}</p>
        <p className="text-xs text-slate-500">{zh ? "普通销售请在客户线索表单 / 客户详情中登记介绍人；佣金相关事宜请联系销售经理或销售副总。" : "Sales reps can still add a referrer on the lead form / lead detail."}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="referral-page" data-tab={tab}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center"><Handshake className="h-5 w-5" /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{zh ? "转介绍 / 合作伙伴佣金" : "Referral Partners & Commissions"}</h1>
          <p className="text-xs text-slate-500">{zh ? "老客户 · 地产经纪人 · 保险经纪人 · 推荐 → 签约锁定规则 → 达成配置的条件 → 财务办理" : "Old customers · Realtors · Insurance agents · referral → lock rules at signing → meet configured trigger → finance payout"}</p>
        </div>
      </div>
      <div className="flex gap-1 flex-wrap border-b border-slate-200" data-testid="referral-tabs">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`referral-tab-${t.key}`} data-on={tab === t.key ? 1 : 0} onClick={() => go(t.key)}
            className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-2 text-sm rounded-t-lg border-b-2 -mb-px transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-700 font-semibold bg-white" : "border-transparent text-slate-500 hover:text-slate-800"}`)}>{zh ? t.zh : t.en}</button>
        ))}
      </div>
      {tab === "overview" && <ReferralOverview {...common} />}
      {tab === "partners" && <PartnersTab {...common} />}
      {tab === "applications" && <ApplicationsTab {...common} />}
      {tab === "cases" && <CasesTab {...common} />}
      {tab === "commissions" && <CommissionsTab {...common} mode="ledger" />}
      {tab === "finance" && <CommissionsTab {...common} mode="finance" />}
      {tab === "rules" && <RulesTab {...common} />}
      {tab === "reports" && <ReportsTab {...common} />}
    </div>
  );
}
